import { useEffect, useState } from 'react';
import { resolveRouteAnimationPrefix } from '@/config/animationRegistry';

/**
 * Animation preferences — global + per-route overrides (localStorage).
 * Effective ON = global ON AND current route override ON (default true).
 * `body.animations-off` reflects effective state for the active page.
 */

const GLOBAL_STORAGE_KEY = 'kiosk.animationsEnabled';
const ROUTE_STORAGE_KEY = 'kiosk.animationsByRoute';

type GlobalListener = (enabled: boolean) => void;
type RouteListener = () => void;

const globalListeners = new Set<GlobalListener>();
const routeListeners = new Set<RouteListener>();

export type RouteAnimationOverrides = Record<string, boolean>;

export function getAnimationsEnabled(): boolean {
  try {
    const raw = localStorage.getItem(GLOBAL_STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

export function getRouteAnimationOverrides(): RouteAnimationOverrides {
  try {
    const raw = localStorage.getItem(ROUTE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as RouteAnimationOverrides;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function isRouteAnimationsEnabled(
  route: string,
  overrides: RouteAnimationOverrides = getRouteAnimationOverrides()
): boolean {
  return overrides[route] ?? true;
}

export function getRouteAnimationsEnabled(pathname: string): boolean {
  const prefix = resolveRouteAnimationPrefix(pathname);
  if (!prefix) return true;
  return isRouteAnimationsEnabled(prefix);
}

export function getEffectiveAnimationsEnabled(pathname: string): boolean {
  return getAnimationsEnabled() && getRouteAnimationsEnabled(pathname);
}

export function applyAnimationsEnabled(enabled: boolean): void {
  if (typeof document !== 'undefined') {
    document.body.classList.toggle('animations-off', !enabled);
  }
}

export function setAnimationsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(GLOBAL_STORAGE_KEY, String(enabled));
  } catch {
    /* private mode */
  }
  globalListeners.forEach((l) => l(enabled));
}

export function setRouteAnimationsEnabled(route: string, enabled: boolean): void {
  const overrides = getRouteAnimationOverrides();
  overrides[route] = enabled;
  try {
    localStorage.setItem(ROUTE_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    /* private mode */
  }
  routeListeners.forEach((l) => l());
}

function subscribeGlobal(listener: GlobalListener): () => void {
  globalListeners.add(listener);
  return () => {
    globalListeners.delete(listener);
  };
}

function subscribeRoute(listener: RouteListener): () => void {
  routeListeners.add(listener);
  return () => {
    routeListeners.delete(listener);
  };
}

/** React hook: [globalEnabled, setGlobalEnabled] */
export function useAnimationsEnabled(): [boolean, (enabled: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(getAnimationsEnabled);

  useEffect(() => subscribeGlobal(setEnabled), []);

  return [enabled, setAnimationsEnabled];
}

/** React hook — live map of per-route overrides. */
export function useRouteAnimationOverrides(): RouteAnimationOverrides {
  const [overrides, setOverrides] = useState<RouteAnimationOverrides>(getRouteAnimationOverrides);

  useEffect(() => subscribeRoute(() => setOverrides(getRouteAnimationOverrides())), []);

  return overrides;
}

if (typeof window !== 'undefined') {
  applyAnimationsEnabled(getEffectiveAnimationsEnabled(window.location.pathname));
}
